import { useContext, useEffect, useState } from 'react';
import {
  fetchLicensedStudentModules,
  GroupModuleYearUnwind,
  linkStudentModuleToGroup,
  unlinkStudentModuleFromGroup,
} from '../../../../../../api/my-groups';
import AddMaterialModal from '../../add-material/AddMaterialModal.vo';
import ModalContext from '../../../../../../contexts/ModalContext';
import { buildGroupProps } from '../../../../../../utils/elementisUtils';
import useThrowAsyncError from '../../../../../../hooks/useThrowAsyncError';
import { DeleteMaterialConfirmationModal } from '../../delete-group-confirmation/DeleteMaterialConfirmationModal.vo';
import { Group } from '../../../../../../api/groups';
import { useTranslation } from 'react-i18next';
import { SchoolYear } from '../../../../hooks/useGroups';
import { useDefaultToast } from '../../../../../../hooks/useDefaultToast';

import List from '@pelckmans/elementis/components/list';
import Pill from '@pelckmans/elementis/components/pill';
import Button from '@pelckmans/elementis/components/button';
import Card from '@pelckmans/elementis/components/card';
import TrashIcon from '@pelckmans/elementis/icons/trash';
import MaterialIcon from '@pelckmans/elementis/icons/material-media';
import PlusIcon from '@pelckmans/elementis/icons/plus';

interface Props {
  linkedModules: GroupModuleYearUnwind[];
  group: Group;
  onModulesAdded: (modules: GroupModuleYearUnwind[]) => void;
  schoolyear: SchoolYear;
}

export default function EditGroupModalMaterial({ linkedModules, group, onModulesAdded }: Props) {
  const [licensedModules, setLicensedModules] = useState<GroupModuleYearUnwind[] | null>(null);
  const { showModal, closeModal } = useContext(ModalContext);
  const handleError = useThrowAsyncError();
  const { REACT_APP_P_API_URL } = process.env;
  const { t } = useTranslation();
  const { failedToast, successToast } = useDefaultToast();

  useEffect(() => {
    async function fetchModules() {
      try {
        const modules = await fetchLicensedStudentModules();
        setLicensedModules(modules);
      } catch (error) {
        handleError(error);
      }
    }

    fetchModules();
  }, [handleError]);

  const onDelete = async (module: GroupModuleYearUnwind) => {
    try {
      await unlinkStudentModuleFromGroup(group.id, module.id);
      const updatedModules = linkedModules.filter(m => m.id !== module.id);
      onModulesAdded(updatedModules);
      closeModal('delete-module-confirmation-modal');
      successToast();
    } catch (error) {
      failedToast();
    }
  };

  const handleModuleSelection = async (modules: GroupModuleYearUnwind[]) => {
    try {
      await linkStudentModuleToGroup(
        group.id,
        modules.map(m => m.id),
      );
      onModulesAdded([...linkedModules, ...modules]);
      successToast();
    } catch (error) {
      failedToast();
    }
  };

  const handleDelete = async (module: GroupModuleYearUnwind) => {
    if (group.status === 'published') {
      showModal(
        <DeleteMaterialConfirmationModal id="delete-module-confirmation-modal" onConfirm={() => onDelete(module)} />,
      );
    } else {
      onDelete(module);
    }
  };

  if (!licensedModules) return null;

  return (
    <div className="material-modules">
      <List emptyMessage={t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.MATERIAL.LIST.EMPTY_STATE')}>
        <List.Header
          buttonGroupProps={buildGroupProps(
            <Button
              startIcon={<PlusIcon />}
              prominence="secondary"
              onClick={() => {
                showModal(
                  <AddMaterialModal
                    id="add-vo-material-modal"
                    licensedModules={licensedModules.filter(
                      licensedModule => !linkedModules.find(m => m.id === licensedModule.id),
                    )}
                    onSelectedModules={modules => handleModuleSelection(modules)}
                  />,
                );
              }}
            >
              {t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.MATERIAL.LIST.ADD_MATERIAL')}
            </Button>,
          )}
          title={t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.MATERIAL.LIST.TITLE')}
          titleTag="h4"
          pill={<Pill icon={<MaterialIcon />}>{linkedModules.length}</Pill>}
        />

        {linkedModules.map(module => (
          <Card
            image={`${REACT_APP_P_API_URL}${module.image}`}
            imagePosition="left"
            key={module.id}
            titleText={module.catalogName}
            titleTag="h3"
            as="container"
            buttonGroupProps={buildGroupProps(
              <Button
                onClick={() => handleDelete(module)}
                aria-label={t('PAGES.MY_GROUPS.MODALS.EDIT_GROUP.MATERIAL.LIST.DELETE.A11Y_LABEL')}
                as="button"
                startIcon={<TrashIcon />}
                prominence="danger"
              />,
            )}
          />
        ))}
      </List>
    </div>
  );
}
